import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { clientOnboardingType } from "../../validator";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import Sparkles from "@/components/icons/SparksIcon";
import { useMutation } from "@tanstack/react-query";
import {
  generateBlog,
  generateImage,
  saveBlog as saveBlogApi,
  saveUser as saveUserApi,
  // saveBlog,
} from "@/http";
import useCurrentUserStore from "@/store";
import { Loader2 } from "lucide-react";
import { GenerateBlogPrompt, GenerateImagePrompt } from "@/types";
import useSonner from "@/hooks/use-sonner";
import slugify from "react-slugify";

type MutationError = unknown;
type GenerateBlogResponse = Awaited<ReturnType<typeof generateBlog>>;
type GenerateImageResponse = Awaited<ReturnType<typeof generateImage>>;

interface GenerateParams {
  prompt: string;
  systemPrompt: string;
  token: string;
  appId?: string;
}
type PropTypes = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<clientOnboardingType, any, undefined>;
  nextTrigger: VoidFunction;
};

const Step3Content: React.FC<PropTypes> = (props) => {
  const [clickNext, setClickNext] = useState<boolean>(false);
  // const hasTitleChanged = props.form.watch('step3.blogTitle')
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [generatedImage, setGeneratedImage] = useState<string>("");
  const {
    appId,
    appSlug,
    userData,
    setGeneratedBlog,
    generatedBlog,
    userCreated,
    setUserCreated,
    selectedApp,
    setBlogImage,
    setIsBlogGenerating,
  } = useCurrentUserStore();

  const { addToast } = useSonner();

  const handleNextStep = async () => {
    setClickNext(true);

    try {
      if (userCreated) {
        await saveBlog.mutateAsync({
          url: selectedApp?.url || "",
          title:
            JSON.parse(generatedBlog)?.title ||
            props.form.getValues("step3.blogTitle"),
          content: JSON.parse(generatedBlog)?.blog_content || "",
          status: "PUBLISHED",
          appName: appSlug || props.form.getValues("step2.slug"),
          authId: userData?.id,
          coverImage: generatedImage || "",
          slug: slugify(
            JSON.parse(generatedBlog)?.title ||
              props.form.getValues("step3.blogTitle")
          ),
          tagTitle: JSON.parse(generatedBlog)?.tags || [],
          metaTitle: JSON.parse(generatedBlog)?.title_tag,
          description: JSON.parse(generatedBlog)?.meta_description,
          token: userData?.access_token_user,
        });
      } else {
        await saveUser.mutateAsync({
          url: selectedApp?.url || "",
          appName: appSlug || props.form.getValues("step2.slug"),
          avatar: userData?.avatar,
          first_name: userData?.first_name,
          last_name: userData?.last_name,
          email: userData?.email,
          id: userData?.id,
        });
      }
    } catch (err) {
      handleMutationError(
        err,
        "Something went wrong while proceeding to the next step."
      );
    } finally {
      setClickNext(false);
    }
  };

  // Centralized error handling
  const handleMutationError = (err: unknown, defaultMessage: string) => {
    const animateDiv = document.getElementById("animate-div");
    if (animateDiv) {
      animateDiv.classList.remove("animate-pulse");
    }
    setIsLoading(false);
    addToast({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      message: (err as any)?.response?.data?.data?.message || defaultMessage,
      type: "error",
    });
  };

  // Save user to the database
  const saveUser = useMutation<
    Awaited<ReturnType<typeof saveUserApi>>, // Response type
    unknown,
    {
      first_name: string;
      last_name: string;
      email: string;
      id: string;
      avatar: string;
      appName: string;
      url: string;
    }
  >({
    mutationFn: saveUserApi,
    onSuccess: async (res) => {
      if (res.data.success) {
        setUserCreated(true);
        await saveBlog.mutateAsync({
          slug: slugify(
            JSON.parse(generatedBlog)?.title ||
              props.form.getValues("step3.blogTitle")
          ),
          title:
            JSON.parse(generatedBlog)?.title ||
            props.form.getValues("step3.blogTitle"),
          url: selectedApp?.url || "",
          content: JSON.parse(generatedBlog)?.blog_content || "",
          status: "PUBLISHED",
          appName: appSlug || props.form.getValues("step2.slug"),
          authId: userData?.id,
          coverImage: generatedImage || "",
          tagTitle: JSON.parse(generatedBlog)?.tags || [],
          metaTitle: JSON.parse(generatedBlog)?.title_tag,
          description: JSON.parse(generatedBlog)?.meta_description,
          token: userData?.access_token_user,
        });
      }
    },
    onError: (err) =>
      handleMutationError(err, "Failed to save user to the database."),
  });

  // Save blog to the database
  const saveBlog = useMutation<
    Awaited<ReturnType<typeof saveBlogApi>>, // Response type
    unknown,
    {
      title: string;
      content: string;
      token: string;
      authId: string;
      status: string;
      metaTitle: string;
      description: string;
      appName: string;
      slug: string;
      tagTitle: string[];
      coverImage: string;
      url: string;
    }
  >({
    mutationFn: saveBlogApi,
    onSuccess: (res) => {
      const animateDiv = document.getElementById("animate-div");
      if (animateDiv) {
        animateDiv.classList.remove("animate-pulse");
      }
      setIsLoading(false);
      props.form.setValue(
        "step3.blogContent",
        JSON.parse(generatedBlog)?.blog_content || ""
      );
      addToast({
        message: res.data.data.message || "Blog generated successfully.",
        type: "success",
      });
      props.form.clearErrors();
      props.nextTrigger();

      const denormalize = JSON.parse(generatedBlog);
      setGeneratedBlog(
        JSON.stringify({
          ...denormalize,
          generatedImage,
        })
      );
    },
    onError: (err) =>
      handleMutationError(err, "Failed to save blog to the database."),
  });

  const generateBlogFromProvider = useMutation<
    GenerateBlogResponse,
    MutationError,
    GenerateParams
  >({
    mutationFn: generateBlog,
    onSuccess: (res) => {
      const result = JSON.parse(res.data?.data?.result || "{}");
      props.form.setValue("step3.blogTitle", result?.title || "");
      props.form.setValue("step3.blogContent", result?.blog_content || "");
      setGeneratedBlog(res.data?.data?.result);
    },
  });

  const generateImageFromProvider = useMutation<
    GenerateImageResponse,
    MutationError,
    GenerateParams
  >({
    mutationFn: generateImage,
    onSuccess: (res) => {
      const imageURL = res.data?.data?.result?.keys[0]?.publicURL;
      setGeneratedImage(imageURL);
      setBlogImage(imageURL);
    },
  });

  // Helper to generate prompts
  const generatePrompt = (type: "blog" | "image", title: string) =>
    type === "blog"
      ? GenerateBlogPrompt({ title })
      : GenerateImagePrompt({ title });

  const handleNextClick = async () => {
    const Step3Data = props.form.getValues("step3");

    if (!Step3Data?.blogTitle) return;

    setIsBlogGenerating(true);
    setIsLoading(true);
    const animateDiv = document.getElementById("animate-div");
    if (animateDiv) {
      animateDiv.classList.add("animate-pulse");
    }

    try {
      const blogPrompt = generatePrompt(
        "blog",
        props.form.getValues("step3.blogTitle")
      );
      const imagePrompt = generatePrompt(
        "image",
        props.form.getValues("step3.blogTitle")
      );
      const token = useCurrentUserStore.getState().dashboardAccessToken || "";

      // New flow: identity JWT straight to the Provider; Profile Manager resolves
      // model + pricing per modality (no action list, no gate tokens).
      await Promise.all([
        generateBlogFromProvider.mutateAsync({
          prompt: blogPrompt,
          systemPrompt: blogPrompt,
          token,
          appId,
        }),
        generateImageFromProvider.mutateAsync({
          prompt: imagePrompt,
          systemPrompt: imagePrompt,
          token,
          appId,
        }),
      ]);
    } catch (err) {
      console.error("Error in handleNextClick:", err);
      addToast({
        message: err instanceof Error ? err.message : "Something went wrong!",
        type: "error",
      });
    } finally {
      const animateDiv = document.getElementById("animate-div");
      if (animateDiv) {
        animateDiv.classList.remove("animate-pulse");
      }
      setIsLoading(false);
      setIsBlogGenerating(false);
    }
  };

  const examples = [
    {
      selected: false,
      content: "The Benefits of AI in Small Business Growth",
    },
    {
      selected: false,
      content: "Top 5 Marketing Trends Powered by AI",
    },
    {
      selected: false,
      content: "How AI is Transforming Healthcare",
    },
    {
      selected: false,
      content: "AI-Driven Personalization in E-commerce",
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <h2 className="font-semibold text-[28px]/[32px]">
          Generate Your First AI-Powered Blog Post
        </h2>
        <p className="text-sm text-muted-foreground">
          Choose a predefined prompt or enter your own to generate AI content.
        </p>
      </div>
      <div className="flex flex-col gap-6">
        {/* Topic Name */}
        <FormField
          control={props.form.control}
          name="step3.blogTitle"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Blog Topic</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  defaultValue={props.form.getValues("step3.blogTitle")}
                  disabled={!generateBlog?.length}
                  placeholder="Enter your blog topic or idea"
                  className={cn("w-full", {
                    "border-destructive focus-visible:ring-transparent":
                      fieldState.error,
                  })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* examples */}
        <span className="text-sm font-medium">Example Topic</span>
        <div className="flex flex-col gap-5 cursor-pointer">
          {examples
            .filter((example) => !example.selected)
            .map((example, index) => (
              <div
                key={index}
                className={cn(
                  "bg-muted px-2 py-3 text-sm rounded-[6px] font-medium",
                  {
                    "disabled bg-muted cursor-not-allowed text-gray-400 pointer-events-none":
                      !generateBlog?.length,
                  }
                )}
                onClick={() => {
                  props.form.setValue("step3.blogTitle", example.content);
                }}
              >
                {example.content}
              </div>
            ))}
        </div>
      </div>

      <Button
        id="app-creation-step3"
        type="submit"
        className="w-full flex items-center justify-center gap-[10px] mt-6"
        onClick={() => {
          if (!generatedBlog.length) {
            handleNextClick();
          } else {
            handleNextStep();
          }
        }}
        disabled={isLoading || clickNext}
      >
        {isLoading || clickNext ? (
          <Loader2 className="animate-spin" />
        ) : (
          generatedBlog === "" && <Sparkles />
        )}
        <span>
          {generatedBlog !== "" && generatedImage !== "" ? "Next" : "Generate"}
        </span>
      </Button>
    </div>
  );
};

export default Step3Content;
